import React, { useState } from 'react';
import styled from 'styled-components';
import { RouteComponentProps, useHistory } from 'react-router';
import AntdForm from 'antd/es/form';
import { ErrorBoundary } from '@allenai/varnish';
import { FormListFieldData } from 'antd/es/form/FormList';
import message from 'antd/es/message';
import Button from 'antd/es/button';
import moment from 'moment';
import { AxiosError } from 'axios';
import Alert from 'antd/es/alert';
import Modal from 'antd/es/modal';

import { paperFormSchema, fetchPaperFromS2 } from '../../api/paper';
import { createAsset } from '../../api';
import { MaxWidthContainer } from '../../components/Layout';
import { EditablePageHeader } from '../../components';
import { makeRules } from '../../utils/customFormUtils';
import { Form, FormErrors } from '../../components/form/Form';
import { TextAreaInput, TextInput } from '../../components/form/Text';
import { MultiSelectInput } from '../../components/form/Select';
import { DateInput } from '../../components/form/Date';
import { ListInput } from '../../components/form/List';
import { FormItem } from '../../components/form/shared';
import { ChangeDisclaimer } from '../../components/form/ChangeDisclaimer';

const NewPaperForm = () => {
    const history = useHistory();
    // let the Swagger API decide what is required
    const ruleMaker = makeRules(paperFormSchema.required);
    // let the Swagger API define restricted multi-select options
    const teamOptions = paperFormSchema.properties.tags.items.enum;
    const collaboratorOptions = paperFormSchema.properties.collaborators.items.enum;

    const [form] = AntdForm.useForm();
    const [errors, setErrors] = useState<string[][]>([]);
    const [isFetching, setIsFetching] = useState(false);
    const [isLinkDirty, setIsLinkDirty] = useState(false);
    const [paperLink, setPaperLink] = useState('');

    const [showConfirmPopup, setShowConfirmPopup] = useState(false);
    const [hasConfirmedEmptyCollaborators, setHasConfirmedEmptyCollaborators] = useState(false);

    const onSubmit = async (data: any) => {
        // convert moment to date
        const formatted = {
            ...data,
            date: moment(data.date).format('YYYY-MM-DD'),
        };
        // we want to promote collaborators being
        // entered. This prompts the user to confirm
        // that there are truly no collaborators
        // when they leave collaborators empty.
        if (!data.collaborators?.length && !hasConfirmedEmptyCollaborators) {
            setShowConfirmPopup(true);
            return;
        }
        try {
            await createAsset('papers', formatted);
            // go back if the request succeeds
            history.goBack();
        } catch (e: any) {
            message.error(`Error saving data: ${e.message}`);
        }
    };

    const fillFromS2 = async () => {
        setIsFetching(true);
        const supportedDomains = [
            'semanticscholar.org',
            'arxiv.org',
            'aclweb.org',
            'acm.org',
            'biorxiv.org',
        ];
        const isSupportedUrl = supportedDomains.some((domain) => paperLink.includes(domain));
        if (!isSupportedUrl) {
            message.error(
                `The provided website URL is not supported for auto-population. We support: ${supportedDomains.join(
                    ', '
                )}`
            );
        } else {
            const paperId = `URL:${paperLink}`;
            try {
                const paperInfo = await fetchPaperFromS2(paperId);
                form.setFieldsValue(paperInfo.data);
                setIsLinkDirty(false);
            } catch (e: unknown | AxiosError) {
                if ((e as AxiosError).isAxiosError) {
                    message.error(`Error: ${(e as AxiosError).response?.data?.error}`);
                } else {
                    message.error(`Error fetching paper details from S2: ${(e as Error).message}`);
                }
            }
        }
        setIsFetching(false);
    };

    return (
        <>
            <Modal
                visible={showConfirmPopup}
                title="Caution: No collaborating institutions were provided"
                cancelText="Return to Add Collaborators"
                onCancel={() => {
                    setHasConfirmedEmptyCollaborators(false);
                    setShowConfirmPopup(false);
                }}
                okText="Submit Without Collaborators"
                onOk={() => {
                    setHasConfirmedEmptyCollaborators(true);
                    setShowConfirmPopup(false);
                    form.submit();
                }}>
                <p>
                    It is important for AI2 to track collaborating institutions accurately. Are you
                    positive this paper does not have any?
                </p>
            </Modal>
            {/* We have to show the errors separately outside of the form in order to consolidate the errors between the two forms below */}
            {!!errors?.length && (
                <ErrorsWrapper>
                    <FormErrors errors={errors} />
                </ErrorsWrapper>
            )}
            {/* This is separated into it's own form so that we can allow users to hit 'enter' to fetch the data instead of accidentally submitting the entire paper form */}
            <AntdForm layout="vertical">
                <Emphasis>
                    <TextInput
                        onChange={(e) => {
                            setIsLinkDirty(true);
                            setPaperLink(e.target.value);
                            // pass on the link value to the hidden input so a button press is not required
                            form.setFieldsValue({ link: e.target.value });
                        }}
                        label="Paper Link"
                        name="link"
                        description={`Provide the Semantic Scholar URL of the published paper. If the paper is not available on Semantic Scholar, use the arXiv link.
                NOTE: Please watch out for stray content when copy/pasting.`}
                        rules={ruleMaker('link', true)}
                        placeholder="https://www.semanticscholar.org/paper/88bb0a28bb58d847183ec505dda89b63771bb495"
                    />
                    <FormItem key="btn-group" hidden={!isLinkDirty}>
                        <StyledAlert
                            type="warning"
                            showIcon
                            message="Warning"
                            description={`Select fields are populated using the Semantic Scholar API. Please double-check and correct EACH SECTION below for accuracy. This includes things like author name formatting, capitalization of venues, and superfluous words like "Abstract" at the beginning of the abstract.`}
                        />
                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={isFetching}
                            onClick={() => fillFromS2()}>
                            Populate from Semantic Scholar
                        </Button>
                    </FormItem>
                </Emphasis>
            </AntdForm>
            <Form
                onSubmit={onSubmit}
                form={form}
                showErrors={false}
                onErrorsFound={(errors) => setErrors(errors)}>
                {/* This is a hidden input whose value is controlled by the auto-populate input field. */}
                <TextInput
                    hidden={true}
                    onChange={(e) => {
                        setIsLinkDirty(true);
                        setPaperLink(e.target.value);
                    }}
                    name="link"
                    rules={ruleMaker('link', true)}
                    value={paperLink}
                />
                <TextInput
                    label="Title"
                    name="title"
                    rules={ruleMaker('title')}
                    description="This is the paper's full title."
                    placeholder="Great science paper"
                />

                <TextInput
                    label="Contributing Authors"
                    name="authors"
                    description={`Comma-separated list of authors full names, such as "Alice Foo, Bob Bar, et al."
                NOTE: Please watch out for stray content when copy/pasting.`}
                    rules={ruleMaker('authors')}
                    placeholder="Alice Foo, Bob Bar, et al."
                />

                <MultiSelectInput
                    label="Teams"
                    name="tags"
                    description="Select one or more teams that were involved with the paper."
                    rules={ruleMaker('tags')}
                    placeholder={[teamOptions[0]]}
                    options={teamOptions}
                />

                <MultiSelectInput
                    label="Collaborating Institutions"
                    name="collaborators"
                    description="Select one or more collaborating institutions that were involved with the paper."
                    rules={ruleMaker('collaborators')}
                    placeholder={[collaboratorOptions[0]]}
                    options={collaboratorOptions}
                />
                <ExtraNote>
                    If there is a collaborating institution that is not listed in the dropdown,
                    please add it to{' '}
                    <a
                        href="https://docs.google.com/spreadsheets/d/1GEMbDJsBpwvn1OWJzILiiqYwpy5wfervnYMEFTeqcNM/edit#gid=0"
                        target="_blank"
                        rel="noreferrer">
                        this spreadsheet
                    </a>{' '}
                    along with the paper name and we'll get it added into the system.
                </ExtraNote>

                <TextAreaInput
                    label="Abstract"
                    name="abstract"
                    description={`The abstract from the paper.
                NOTE: Double-check the abstract text for any odd formatting or characters that didn't copy
                over correctly from the PDF.`}
                    rules={ruleMaker('abstract')}
                    placeholder="We present groundbreaking results about ..."
                />

                <DateInput
                    label="Publication Date"
                    name="date"
                    description={`The date on which the paper was published. This is the first day of the conference for
                which the paper was accepted, or the publication date of the article if not a
                conference-published paper.`}
                    rules={ruleMaker('date')}
                    placeholder="2021-06-01"
                />

                <TextInput
                    label="Venue"
                    name="venue"
                    description={`For conferences, use the acronym excluding the year. If the paper was part of a
                workshop, put a bullet (Option + 8 on a Mac) between conference and workshop such as
                "CONF • workshopName". If the paper is only on arXiv, just put "arXiv".`}
                    rules={ruleMaker('venue')}
                    placeholder="CONF • workshopName"
                />

                <ListInput
                    label="Datasets"
                    name="datasetUrls"
                    description="URLs to datasets associated with the paper."
                    rules={ruleMaker('datasetUrls', true)}
                    childInput={(props: FormListFieldData) => (
                        <TextInput
                            {...props}
                            rules={ruleMaker('datasetUrls', true)}
                            placeholder="https://beaker.org/datasets"
                        />
                    )}
                />

                <ListInput
                    label="Demos"
                    name="demoUrls"
                    description="URLs to demos associated with the paper."
                    childInput={(props: FormListFieldData) => (
                        <TextInput
                            {...props}
                            rules={ruleMaker('demoUrls', true)}
                            placeholder="https://skiff.allenai.org/"
                        />
                    )}
                />

                <ListInput
                    label="Leaderboards"
                    name="leaderboardUrls"
                    description="URLs to leaderboards associated with the paper."
                    childInput={(props: FormListFieldData) => (
                        <TextInput
                            {...props}
                            rules={ruleMaker('leaderboardUrls', true)}
                            placeholder="https://leaderboard.allenai.org/"
                        />
                    )}
                />

                <ListInput
                    label="Podcasts"
                    name="podcastUrls"
                    description="URLs to podcasts associated with the paper."
                    childInput={(props: FormListFieldData) => (
                        <TextInput
                            {...props}
                            rules={ruleMaker('podcastUrls', true)}
                            placeholder="https://www.stitcher.com/"
                        />
                    )}
                />

                <ListInput
                    label="Code"
                    name="codeUrls"
                    description="URLs to code associated with the paper."
                    childInput={(props: FormListFieldData) => (
                        <TextInput
                            {...props}
                            rules={ruleMaker('codeUrls', true)}
                            placeholder="https://www.github.com"
                        />
                    )}
                />

                <ListInput
                    label="Videos"
                    name="videoUrls"
                    description="URLs to videos associated with the paper."
                    childInput={(props: FormListFieldData) => (
                        <TextInput
                            {...props}
                            rules={ruleMaker('codeUrls', true)}
                            placeholder="https://www.youtube.com"
                        />
                    )}
                />

                <TextInput
                    label="Award Name"
                    name="awardName"
                    description={`Provide the name of any award this paper received, e.g. "Best Paper Award" or
                "People's Choice Award". This automatically adds an "Award Winner" banner to the paper
                when it's displayed at the AI2 website (https://allenai.org).`}
                    rules={ruleMaker('awardName')}
                    placeholder="Outstanding Paper Award"
                />

                <TextInput
                    label="Award Link"
                    name="awardLink"
                    description={`Provide the URL to a page with details about paper's award, if applicable. For example, a
                conference website might list it on a "Best Paper Awards" page.`}
                    rules={ruleMaker('awardLink', true)}
                    placeholder="https://allenai.org/awards/paper-of-year/2021"
                />
                <ChangeDisclaimer />
            </Form>
        </>
    );
};

const Emphasis = styled.div`
    background-color: ${({ theme }) => theme.palette.background.light};
    padding: ${({ theme }) => theme.spacing.md};
    border: 1px solid ${({ theme }) => theme.palette.border.default};
    margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const StyledAlert = styled(Alert)`
    margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const ExtraNote = styled.p`
    font-style: italic;
    margin-top: 0;
`;

const ErrorsWrapper = styled.div`
    margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

interface Params {
    category: string;
    id?: string;
}

export const NewPaper = (props: RouteComponentProps<Params>) => {
    return (
        <ErrorBoundary key={props.match.params.category}>
            <MaxWidthContainer>
                <EditablePageHeader
                    iconName="paper"
                    iconType="url"
                    label={'New Paper'}
                    history={props.history}
                />
                <NewPaperForm />
            </MaxWidthContainer>
        </ErrorBoundary>
    );
};
